import { Lock, Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { OuvirPageExample } from '../../../data/portfolio-examples';
import { hasMedia } from '../../../data/portfolio-examples';
import { formatTime } from '../playback';
import EmptyMediaSlot from './EmptyMediaSlot';

type Props = {
  example: OuvirPageExample;
  registerMedia: (id: string, element: HTMLMediaElement | null) => void;
  claimPlayback: (id: string) => void;
};

/**
 * Retrato da entrega com página própria. Mantém a linguagem visual de
 * `src/pages/ouvir/SlugPage.tsx` (fundo plum, cartão de vidro, disco, player,
 * bloco de letra) para o visitante reconhecer a página quando receber o link.
 */
export default function OuvirPageCard({ example, registerMedia, claimPlayback }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const ready = hasMedia(example.audio);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setElapsed(audio.currentTime);
    const onMeta = () => setDuration(audio.duration);
    const onEnd = () => {
      setPlaying(false);
      setElapsed(0);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
    };
  }, [ready]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => {});
    else audio.pause();
  };

  const total = duration || 0;
  const progress = total > 0 ? (elapsed / total) * 100 : 0;
  const totalLabel = total > 0 ? formatTime(total) : example.audio.duration || '--:--';

  return (
    <article className="ex-sample">
      <div className="ex-ouvir">
        <div className="ex-ouvir__urlbar">
          <Lock size={13} strokeWidth={2.4} aria-hidden="true" />
          <span className="ex-ouvir__url">
            inspiramusicas.com.br/ouvir/{example.slug || '…'}
          </span>
        </div>

        <div className="ex-ouvir__screen">
          <div className="ex-ouvir__card">
            {example.cover ? (
              <figure className="ex-ouvir__cover">
                <img src={example.cover} alt="" loading="lazy" decoding="async" />
              </figure>
            ) : (
              <div className="ex-ouvir__cover">
                <EmptyMediaSlot spec="Foto de capa da pessoa" />
              </div>
            )}

            <div className="ex-ouvir__disc" data-playing={playing} aria-hidden="true" />

            <p className="ex-ouvir__eyebrow">Música para</p>
            <p className="ex-ouvir__name">{example.name || 'Nome da pessoa'}</p>

            <div className="ex-ouvir__player">
              <button
                type="button"
                className="ex-ouvir__play"
                onClick={toggle}
                disabled={!ready}
                aria-label={playing ? `Pausar o exemplo de ${example.genre}` : `Ouvir o exemplo de ${example.genre}, ${example.occasion}`}
              >
                {playing ? (
                  <Pause size={17} fill="currentColor" strokeWidth={0} aria-hidden="true" />
                ) : (
                  <Play size={17} fill="currentColor" strokeWidth={0} aria-hidden="true" />
                )}
              </button>

              <div className="ex-ouvir__track">
                <div className="ex-ouvir__bar">
                  <div className="ex-ouvir__fill" style={{ width: `${progress}%` }} />
                </div>
                <div className="ex-ouvir__times">
                  <span>{formatTime(elapsed)}</span>
                  <span>{totalLabel}</span>
                </div>
              </div>
            </div>

            {ready && (
              <audio
                ref={(element) => {
                  audioRef.current = element;
                  registerMedia(example.id, element);
                }}
                src={example.audio.src}
                preload="none"
                onPlay={() => {
                  claimPlayback(example.id);
                  setPlaying(true);
                }}
                onPause={() => setPlaying(false)}
              />
            )}

            {!ready && <EmptyMediaSlot tone="dark" inline spec="MP3 da música completa" />}

            <div className="ex-ouvir__divider" />

            <p className="ex-ouvir__lyrics-label">Letra</p>
            {example.lyrics ? (
              <div className="ex-ouvir__lyrics">
                <pre>{example.lyrics}</pre>
              </div>
            ) : (
              <EmptyMediaSlot spec="Letra completa da música" />
            )}
          </div>
        </div>
      </div>

      <div className="ex-card__meta">
        <span className="ex-card__genre">{example.genre}</span>
        <p className="ex-card__occasion">{example.occasion}</p>
        <p className="ex-card__plan">Página com letra · feita para {example.name}</p>
        {ready && example.audio.duration && (
          <p className="ex-card__duration">Música inteira, {example.audio.duration}</p>
        )}
      </div>
    </article>
  );
}
